import { AuthService, isRefreshAuth, readStoredUserState, type UserState } from '@myosh/myosh-login';
import { toast } from '@myosh/odin-components';
import { t } from '@myosh/odin-translations/i18n';
import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

interface ClusterConfig {
  apiBaseUrl?: string;
  apiKey?: string;
}

const USER_TIME_ZONE = Intl.DateTimeFormat(navigator.language).resolvedOptions().timeZone;
const LOGOUT_DELAY_MS = 3200;

const authService = new AuthService();

const readClusterConfig = (): ClusterConfig => {
  try {
    return JSON.parse(localStorage.getItem('ClusterConfig') || '{}') as ClusterConfig;
  } catch {
    return {};
  }
};

const readUserState = (): UserState | undefined => {
  const stored = readStoredUserState();
  return stored ? (JSON.parse(stored) as UserState) : undefined;
};

const notifyUserAndLogout = (id: string, message: string) => {
  toast.error(message, { id });
  setTimeout(() => void authService.logout(), LOGOUT_DELAY_MS);
};

const renewSession = async () => {
  try {
    const renewed: unknown = await authService.signinSilent();
    return isRefreshAuth(renewed) && Boolean(renewed.refresh_token);
  } catch {
    return false;
  }
};

// The v4 API reads repeated keys (`filter=a&filter=b`); the default serializer joins arrays with commas.
export const serializeParams = (params: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item !== undefined && item !== null) {
        searchParams.append(key, String(item));
      }
    }
  }
  return searchParams.toString();
};

const rawBaseQuery = (baseUrl: string, apiKey: string) =>
  fetchBaseQuery({
    baseUrl,
    paramsSerializer: serializeParams,
    prepareHeaders: (headers) => {
      const userState = readUserState();
      const idToken = userState?.tokens?.id_token;
      const schema = userState?.user?.currentSchema;
      const controlledUsername = userState?.user?.controlledUsername;

      if (idToken) {
        headers.set('authorization', `Bearer ${idToken}`);
      }
      if (schema) {
        headers.set('Myosh-Schema', schema);
      }
      if (userState?.user?.superuser === 'true' && controlledUsername) {
        headers.set('Controlled-Username', controlledUsername);
      }
      headers.set('x-api-key', apiKey);
      headers.set('Myosh-TimeZone', USER_TIME_ZONE);

      return headers;
    },
  });

export const baseQueryWithReAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const clusterConfig = readClusterConfig();
  const baseUrl = clusterConfig.apiBaseUrl || import.meta.env.REACT_APP_API_BASE_URL;
  const apiKey = clusterConfig.apiKey || import.meta.env.REACT_APP_API_KEY;

  if (!baseUrl || !apiKey) {
    notifyUserAndLogout(
      '400',
      t(($) => $.errorLoadingConfiguration)
    );
    return { error: { status: 400, data: 'Missing application configuration' } };
  }

  const query = rawBaseQuery(baseUrl, apiKey);
  let result = await query(args, api, extraOptions);

  if (result.error?.status === 401) {
    if (await renewSession()) {
      result = await query(args, api, extraOptions);
    }
    if (result.error?.status === 401) {
      notifyUserAndLogout(
        '401',
        t(($) => $.error401)
      );
    }
  }

  return result;
};
